/**
 * Feature flag reads (browser).
 *
 * `posthog-js` fetches the whole flag payload for the current distinct id
 * once at init, and again after `identify()` and `reset()`. Reads after
 * that are a lookup in an in-memory object, which is what makes a flag
 * check cheap enough to put in a render path — no network call happens on
 * `isFeatureEnabled()`.
 *
 * A read does queue one `$feature_flag_called` event, which posthog-js
 * dedupes per flag AND value via its `$flag_call_reported` map. That map
 * lives in ordinary persistence, so the dedupe holds until `identify()`
 * or `reset()` clears it, not just for one session. Either way a flag
 * read on every render costs one event and not one per render, which is
 * why `useFeatureFlag` can call through on each `getSnapshot`.
 *
 * The consequence is that flags are UNKNOWN for the first round trip after
 * boot. `getFlag` reports that honestly as `undefined`; `isEnabled` folds
 * it into the flag's catalogued default so a caller that only wants a
 * boolean does not have to invent one. A component that must not flash the
 * wrong branch should read `useFeatureFlag` and render nothing while the
 * value is `undefined`.
 *
 * `./posthog` owns the client and the enabled/disabled decision; this
 * module is the only place that asks it about flags, so a change to how
 * flags are read has one site rather than one per component.
 */

import {
	FEATURE_FLAG_DEFAULTS,
	type FeatureFlagName,
	type FeatureFlagValue,
} from "@shared/types";
import { getPostHogClient } from "./posthog";

/**
 * The raw answer for a flag: a variant key, `true`/`false`, or `undefined`
 * when PostHog is disabled, the flags have not loaded yet, or the project
 * defines no such key. The three are indistinguishable here on purpose —
 * all three mean "we do not know".
 */
export function getFlag(flag: FeatureFlagName): FeatureFlagValue {
	const ph = getPostHogClient();
	if (!ph) return undefined;
	try {
		return ph.getFeatureFlag(flag);
	} catch {
		// fail silent — a flag read must never break a render
		return undefined;
	}
}

/**
 * The flag as a boolean, with the catalogued default standing in for any
 * answer PostHog could not give. A multivariate flag reads as `true` for
 * every variant other than the disabled state, matching PostHog's own
 * `isFeatureEnabled` semantics.
 */
export function isEnabled(flag: FeatureFlagName): boolean {
	const ph = getPostHogClient();
	if (!ph) return FEATURE_FLAG_DEFAULTS[flag];
	try {
		return ph.isFeatureEnabled(flag) ?? FEATURE_FLAG_DEFAULTS[flag];
	} catch {
		return FEATURE_FLAG_DEFAULTS[flag];
	}
}

/**
 * Subscribe to flag payload arrivals. PostHog calls back on the first
 * load and after every reload, which happens on `identify()`, on
 * `reset()`, on an explicit `reloadFeatureFlags()`, and on posthog-js's
 * own poll — every `remote_config_refresh_interval_ms`, 5 minutes by
 * default, and skipped entirely while the tab is hidden.
 *
 * So a flag flipped in the dashboard DOES reach an already-open tab, but
 * within minutes rather than at once, and never in a background tab. If
 * you flip a flag and nothing happens, wait out the interval or reload
 * before concluding the wiring is broken.
 *
 * Returns an unsubscribe function. When PostHog is disabled the callback
 * never fires and the returned function is a no-op, so a caller can
 * always treat this as a normal subscription.
 */
export function onFlagsChanged(callback: () => void): () => void {
	const ph = getPostHogClient();
	if (!ph) return () => {};
	try {
		return ph.onFeatureFlags(() => callback());
	} catch {
		return () => {};
	}
}
